import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  OPS_SERVICE_SLUGS,
  OPS_SLO_TARGET_PERCENT,
  isOpsSloBreached,
  isOpsSloRateOnTarget,
  opsBugRate,
  opsCountBreaching,
  opsCountOpen,
  opsDisplayNo,
  opsSloOverDays,
  opsSloPeriodOptions,
  opsSloRate,
  opsTotalCount,
  opsTotalPoint,
  type OpsSloTrendMonths,
} from '@cortex/ops-core';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { TabButton } from '../components/ui/Tabs/TabButton.js';
import { TabGroup } from '../components/ui/Tabs/TabGroup.js';
import {
  OpsAggregatingPanel,
  OpsFetchErrorPanel,
} from '../components/features/ops/OpsDashboardStates.js';
import { OpsMemberBreakdownCard } from '../components/features/ops/OpsMemberBreakdownCard.js';
import { OpsSendBackBreakdownCard } from '../components/features/ops/OpsSendBackBreakdownCard.js';
import { OpsSloTrendChart } from '../components/features/ops/OpsSloTrendChart.js';
import { useOpsSlo, useOpsSloStateHistory, useOpsSloTrend } from '../hooks/useOps.js';
import { OPS_SERVICE_LABELS } from '../lib/ops-fixtures.js';
import { formatOpsMetric as fmt } from '../lib/ops-metric-format.js';
import type { OpsService } from '../types/ops.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops SLO ダッシュボード。SLO / 内訳 の 2 タブで、期間（月／四半期）とサービス絞り込み（All / 各サービス）は両タブ共通。内訳タブは担当者別の 対応件数・工数・期限超過率・平均処理営業日数 と、サービス別の 差戻し率・調査再開までの平均（土日除外）・再開せず完了・保留中・差戻し理由の内訳（ステート履歴を /ops/slo/state-history から内訳タブ表示中のみ取得）を表示する。SLO タブは総件数・総工数(point)・未対応件数・SLA 未達件数・SLO 達成率・バグ転換率を集計し、サービス別 SLO 内訳と未達の依頼、直近 6／12 ヶ月の件数／工数／SLO 達成率のボリューム推移チャートを表示する。期間内の依頼は /ops/slo、推移は /ops/slo/trend からライブ取得し、集計はすべて ops-core の共有集計（slo-aggregate）で行う（画面側に集計ロジックを持たない）
 * @graph-connects useOpsSlo [calls] 期間内（当月／当四半期）の依頼一覧の取得
 * @graph-connects useOpsSloTrend [calls] 直近 6／12 ヶ月の依頼一覧の取得（推移チャート）
 * @graph-connects OpsSloTrendChart [references] SLO ボリューム推移チャートを描画
 * @graph-connects OpsMemberBreakdownCard [references] 内訳タブのメンバー別テーブルを描画
 * @graph-connects useOpsSloStateHistory [calls] 内訳タブ表示中に期間内のステート履歴を取得
 * @graph-connects OpsSendBackBreakdownCard [references] 内訳タブのサービス別差戻しテーブルを描画
 */
export const Route = createFileRoute('/ops/dashboard')({
  component: OpsDashboardPage,
});

/** 集計期間。 */
type PeriodKey = 'month' | 'quarter';
/** 表示タブ（SLO / 内訳）。 */
type ViewKey = 'slo' | 'breakdown';
/** サービス絞り込み（all = 全サービス）。 */
type ServiceFilter = 'all' | OpsService;

/** SLO 内訳・絞り込み対象のサービス並び（ops-core を単一 source とし、サービス追加に自動追従する）。 @graph-connects none */
const SERVICES: readonly OpsService[] = OPS_SERVICE_SLUGS;

/** メトリクス値の色トーン。 @graph-connects none */
type MetricTone = 'default' | 'success' | 'error';

/** トーン → 値のテキスト色クラス。 @graph-connects none */
function toneClass(tone: MetricTone): string {
  if (tone === 'success') return 'text-[var(--color-success)]';
  if (tone === 'error') return 'text-[var(--color-error)]';
  return 'text-[var(--color-text-primary)]';
}

/**
 * SLO 達成率（%）→ 目標ライン比較のトーン（達成=success / 未達=error / 不明=default）。判定は ops-core。
 * @graph-connects isOpsSloRateOnTarget [calls] 目標ラインとの比較
 */
function sloTone(rate: number | null): MetricTone {
  const onTarget = isOpsSloRateOnTarget(rate);
  if (onTarget === null) return 'default';
  return onTarget ? 'success' : 'error';
}

/** MetricCard の props。 */
interface MetricCardProps {
  /** 見出しラベル。 */
  label: string;
  /** 大きく表示する値。 */
  value: string;
  /** 補足テキスト。 */
  hint?: string;
  /** 値の色トーン（目標比較などに使う）。既定 default。 */
  tone?: MetricTone;
}

/** 単一メトリクスカード。 @graph-connects none */
function MetricCard({ label, value, hint, tone = 'default' }: MetricCardProps): ReactNode {
  return (
    <Card padding="md">
      <div className="text-xs font-medium text-[var(--color-text-muted)]">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${toneClass(tone)}`}>{value}</div>
      {hint !== undefined && (
        <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">{hint}</div>
      )}
    </Card>
  );
}

/** @graph-connects none */
function OpsDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewKey>('slo');
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [periodStart, setPeriodStart] = useState<string>(
    () => opsSloPeriodOptions('month')[0]?.value ?? '',
  );
  const [service, setService] = useState<ServiceFilter>('all');
  const periodOptions = useMemo<SelectOption[]>(() => opsSloPeriodOptions(period), [period]);
  const periodLabel = periodOptions.find((o) => o.value === periodStart)?.label ?? '';
  const { data, isLoading, isError, refetch } = useOpsSlo(period, periodStart);
  // ボリューム推移（件数 / 工数 / SLO 達成率）は期間トグルとは独立に直近 6 / 12 ヶ月を取得する。
  const [trendMonths, setTrendMonths] = useState<OpsSloTrendMonths>(6);
  const trend = useOpsSloTrend(trendMonths);
  // サービス別の差戻し統計の元データ（ステート履歴）は内訳タブを開いたときだけ取得する。
  const stateHistory = useOpsSloStateHistory(period, periodStart, view === 'breakdown');

  // 期間の単位（月 / 四半期）を切り替えたら、その単位の最新期間（先頭）にリセットする。
  const selectGranularity = (next: PeriodKey): void => {
    setPeriod(next);
    setPeriodStart(opsSloPeriodOptions(next)[0]?.value ?? '');
  };

  // 未解決依頼の経過は「今」で測る。1 回の描画の中で全集計が同じ時刻を使うよう 1 度だけ取る。
  const now = new Date();
  // サービス絞り込み（期間の依頼・推移の依頼の両方に同じ条件を当てる）。
  const inService = <T extends { service: OpsService }>(rows: T[]): T[] =>
    service === 'all' ? rows : rows.filter((r) => r.service === service);
  // /ops/slo が期間（起票日基準）で絞り込み済みのため、クライアント側の期間フィルタは不要。
  const periodRows = data?.requests ?? [];
  const scoped = inService(periodRows);
  const trendRows = inService(trend.data?.requests ?? []);
  // SLO 未達（目標超過）の依頼。ライブの「今の逼迫」ではなく期間の SLO 結果（上の SLO / SLA missed）と
  // 一致させるため、件数と同じ isOpsSloBreached で判定し、超過が大きい順に並べる（解決遅延・未解決超過の両方）。
  const missed = scoped
    .filter((r) => isOpsSloBreached(r, now))
    .map((r) => ({ request: r, overDays: opsSloOverDays(r, now) }))
    .sort((a, b) => b.overDays - a.overDays);

  // SLO は「達成率そのもの（絶対値）」を値に出し、色と hint で目標ライン（OPS_SLO_TARGET_PERCENT）との差を示す。
  const scopedSlo = opsSloRate(scoped, now);
  const sloGoalHint =
    scopedSlo === null
      ? t('ops.dashboard.sloGoal.target', { target: OPS_SLO_TARGET_PERCENT })
      : t('ops.dashboard.sloGoal.delta', {
          sign: isOpsSloRateOnTarget(scopedSlo) === true ? '+' : '',
          delta: scopedSlo - OPS_SLO_TARGET_PERCENT,
          target: OPS_SLO_TARGET_PERCENT,
        });

  const serviceOptions: SelectOption[] = [
    { value: 'all', label: t('ops.common.allServices') },
    ...SERVICES.map((s) => ({ value: s, label: OPS_SERVICE_LABELS[s] })),
  ];

  return (
    <div className="space-y-4">
      <TabGroup ariaLabel={t('ops.dashboard.viewLabel')}>
        <TabButton
          active={view === 'slo'}
          label={t('ops.dashboard.views.slo')}
          onClick={() => {
            setView('slo');
          }}
        />
        <TabButton
          active={view === 'breakdown'}
          label={t('ops.dashboard.views.breakdown')}
          onClick={() => {
            setView('breakdown');
          }}
        />
      </TabGroup>

      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <TabGroup ariaLabel={t('ops.dashboard.periodUnitLabel')}>
            <TabButton
              active={period === 'month'}
              label={t('ops.dashboard.month')}
              onClick={() => {
                selectGranularity('month');
              }}
            />
            <TabButton
              active={period === 'quarter'}
              label={t('ops.dashboard.quarter')}
              onClick={() => {
                selectGranularity('quarter');
              }}
            />
          </TabGroup>
          <div className="w-full sm:w-40">
            <Select
              aria-label={t('ops.dashboard.periodLabel')}
              options={periodOptions}
              value={periodStart}
              onChange={(e) => {
                setPeriodStart(e.target.value);
              }}
              fullWidth
              size="sm"
            />
          </div>
        </div>
        <div className="w-full sm:w-56">
          <Select
            aria-label={t('ops.common.serviceFilterLabel')}
            options={serviceOptions}
            value={service}
            onChange={(e) => {
              setService(e.target.value as ServiceFilter);
            }}
            fullWidth
            size="sm"
          />
        </div>
      </div>

      <p className="text-xs text-[var(--color-text-muted)]">
        {t('ops.dashboard.liveSummaryNote', {
          service: service === 'all' ? t('ops.common.allServices') : OPS_SERVICE_LABELS[service],
          period: periodLabel,
        })}
      </p>

      {isError ? (
        <Card padding="md">
          <OpsFetchErrorPanel
            onRetry={() => {
              void refetch();
            }}
          />
        </Card>
      ) : isLoading ? (
        <Card padding="md">
          <OpsAggregatingPanel />
        </Card>
      ) : view === 'breakdown' ? (
        <>
          <OpsMemberBreakdownCard rows={scoped} periodLabel={periodLabel} now={now} />
          <OpsSendBackBreakdownCard
            requests={periodRows}
            events={stateHistory.data?.events}
            periodLabel={periodLabel}
            isLoading={stateHistory.isLoading}
            isError={stateHistory.isError}
            onRetry={() => {
              void stateHistory.refetch();
            }}
          />
        </>
      ) : (
        <>
          {/* Scoped metrics */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              label={t('ops.dashboard.metrics.totalRequests')}
              value={fmt(opsTotalCount(scoped))}
              hint={t('ops.dashboard.metrics.totalRequestsHint')}
            />
            <MetricCard
              label={t('ops.dashboard.metrics.totalPoints')}
              value={fmt(opsTotalPoint(scoped))}
              hint={t('ops.dashboard.metrics.totalPointsHint')}
            />
            <MetricCard
              label={t('ops.dashboard.metrics.openRequests')}
              value={fmt(opsCountOpen(scoped))}
            />
            <MetricCard
              label={t('ops.dashboard.metrics.slaMissed')}
              value={fmt(opsCountBreaching(scoped, now))}
              hint={t('ops.dashboard.metrics.slaMissedHint')}
            />
            <MetricCard
              label={t('ops.dashboard.metrics.sloOnTime')}
              value={fmt(scopedSlo, true)}
              tone={sloTone(scopedSlo)}
              hint={sloGoalHint}
            />
            <MetricCard
              label={t('ops.dashboard.metrics.bugConversion')}
              value={fmt(opsBugRate(scoped), true)}
              hint={t('ops.dashboard.metrics.bugConversionHint')}
            />
          </div>

          {/* Per-service breakdown */}
          <Card padding="none">
            <div className="border-b border-[var(--color-border)] px-4 py-3">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t('ops.dashboard.byService.title', { period: periodLabel })}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="text-left text-xs text-[var(--color-text-muted)]">
                    <th className="px-4 py-2 font-medium">
                      {t('ops.dashboard.byService.columns.service')}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t('ops.dashboard.byService.columns.open')}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t('ops.dashboard.byService.columns.missed')}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t('ops.dashboard.byService.columns.slo')}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t('ops.dashboard.byService.columns.bugConversion')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {SERVICES.map((s) => {
                    const rows = periodRows.filter((r) => r.service === s);
                    const rowSlo = opsSloRate(rows, now);
                    const isSelected = service === s;
                    return (
                      <tr
                        key={s}
                        className={`cursor-pointer border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-hover)] ${
                          isSelected ? 'bg-[var(--color-bg-hover)]' : ''
                        }`}
                        onClick={() => {
                          setService(isSelected ? 'all' : s);
                        }}
                      >
                        <td className="px-4 py-2.5 font-medium text-[var(--color-text-primary)]">
                          {OPS_SERVICE_LABELS[s]}
                        </td>
                        <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">
                          {fmt(opsCountOpen(rows))}
                        </td>
                        <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">
                          {fmt(opsCountBreaching(rows, now))}
                        </td>
                        <td className={`px-2 py-2.5 font-semibold ${toneClass(sloTone(rowSlo))}`}>
                          {fmt(rowSlo, true)}
                        </td>
                        <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">
                          {fmt(opsBugRate(rows), true)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* SLA missed (期間内で目標を超過した依頼。上の SLO / SLA missed と件数が一致する) */}
          <Card padding="none">
            <div className="border-b border-[var(--color-border)] px-4 py-3">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t('ops.dashboard.slaMissedList.title', { count: missed.length })}
              </h2>
            </div>
            {missed.length === 0 ? (
              <div className="p-6 text-center text-sm text-[var(--color-text-muted)]">
                {t('ops.dashboard.slaMissedList.allWithinTarget')}
              </div>
            ) : (
              <ul>
                {missed.map(({ request: r, overDays }) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-2.5 first:border-t-0"
                  >
                    <Link
                      to="/ops/requests/$id"
                      params={{ id: r.id }}
                      className="min-w-0 truncate text-sm text-[var(--color-text-primary)] hover:underline"
                    >
                      <span className="font-semibold text-[var(--color-accent-primary)]">
                        #{opsDisplayNo(r.id)}
                      </span>{' '}
                      <span className="text-[var(--color-text-muted)]">
                        {OPS_SERVICE_LABELS[r.service]} ·
                      </span>{' '}
                      {r.title}
                    </Link>
                    <div className="shrink-0 whitespace-nowrap">
                      <Badge variant="error" size="sm" solid>
                        {t('ops.dashboard.slaMissedList.daysOver', { days: overDays })}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Volume trend (件数 / 工数 / SLO 達成率 の 6 / 12 ヶ月推移) */}
          <OpsSloTrendChart
            rows={trendRows}
            months={trendMonths}
            onMonthsChange={setTrendMonths}
            isLoading={trend.isLoading}
            isError={trend.isError}
            onRetry={() => {
              void trend.refetch();
            }}
          />
        </>
      )}
    </div>
  );
}
